import ReactJson, { type ThemeObject } from '@microlink/react-json-view';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  showToast,
} from '@solinkcorp/solink-ui';
import { useColorTheme } from 'src/hooks/useColorTheme';
import {
  useJSONModalActions,
  type JSONCopyNotifier,
  type JSONModalProps,
} from './useJSONModalActions';

const aspenNotifier: JSONCopyNotifier = {
  success: (message) => {
    showToast.success({ title: message });
  },
  error: (message) => {
    showToast.error({ title: message });
  },
};

/**
 * react-json-view markup. The copy glyph is a sibling that shifts the line when
 * it appears. Pin it after the text, and keep a hidden glyph in flow so the line
 * does not jump. Re-check this if @microlink/react-json-view changes
 * `.copy-icon`, `.copy-to-clipboard-container`, or its inline `display: none`.
 */
const JSON_VIEW_COPY_ICON_FIX = [
  '[&_.copy-icon+span]:!absolute',
  '[&_.copy-icon+span]:left-full',
  '[&_.copy-icon+span]:top-0',
  '[&_.copy-icon+span]:!ml-1',
  '[&_.copy-icon+span]:text-xs',
  '[&_.copy-icon+span]:leading-[18px]',
  '[&_.copy-to-clipboard-container>span>span]:relative',
  "[&_.copy-to-clipboard-container[style*='none']]:invisible",
  "[&_.copy-to-clipboard-container[style*='none']]:!inline-block",
].join(' ');

const ramp = (step: string) => `rgb(var(--${step}))`;

// react-json-view assigns each base16 slot to one role. Use the primitive ramps
// so the well does not flip when `.dark` changes the semantic tokens.
const aspenJSONThemeDark: ThemeObject = {
  base00: ramp('Gray-950'), // background
  base01: ramp('Gray-900'),
  base02: ramp('Gray-800'), // indent guides
  base03: ramp('Gray-700'),
  base04: ramp('Gray-500'), // item counts
  base05: ramp('Gray-500'), // undefined
  base06: ramp('Gray-300'),
  base07: ramp('Gray-100'), // keys and braces
  base08: ramp('Red-400'), // NaN
  base09: ramp('Orange-300'), // strings
  base0A: ramp('Purple-300'), // null
  base0B: ramp('Blue-300'), // floats
  base0C: ramp('Gray-400'), // array indexes
  base0D: ramp('Primary-400'), // expanded chevron, dates, copied check
  base0E: ramp('Primary-400'), // collapsed chevron, booleans
  base0F: ramp('Blue-300'), // integers, copy icon
};

const aspenJSONThemeLight: ThemeObject = {
  base00: ramp('Gray-50'),
  base01: ramp('Gray-100'),
  base02: ramp('Gray-200'),
  base03: ramp('Gray-300'),
  base04: ramp('Gray-700'),
  base05: ramp('Gray-700'),
  base06: ramp('Gray-800'),
  base07: ramp('Gray-900'),
  base08: ramp('Red-700'),
  base09: ramp('Orange-700'),
  base0A: ramp('Purple-600'),
  base0B: ramp('Blue-700'),
  base0C: ramp('Gray-700'),
  base0D: ramp('Primary-700'),
  base0E: ramp('Primary-700'),
  base0F: ramp('Blue-700'),
};

export function JSONModalAspen(props: JSONModalProps) {
  const { isDark } = useColorTheme();
  const theme = isDark ? aspenJSONThemeDark : aspenJSONThemeLight;
  const { locale, serializedSrc, copyJSONToClipboard, title, closeModal } = useJSONModalActions(
    props,
    aspenNotifier,
  );

  return (
    <Dialog
      open={props.isModalOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          closeModal();
        }
      }}
    >
      <DialogContent size="l" className="!flex max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <div
          className={`-mx-6 min-h-0 flex-1 overflow-y-auto ${JSON_VIEW_COPY_ICON_FIX}`}
          style={{ background: theme.base00 }}
        >
          <ReactJson
            src={serializedSrc}
            name={false}
            collapsed={1}
            enableClipboard={true}
            theme={theme}
            style={{ padding: '12px 24px', fontSize: '12px', lineHeight: '18px' }}
            escapeStrings={false}
            displayDataTypes={false}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeModal}>
            {locale.general.cancel}
          </Button>
          <Button type="button" onClick={copyJSONToClipboard}>
            Copy to Clipboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
